import { ExpertProfileSelectDto, ExpertProfileType, UserSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { AreaBarChartComponent } from '@components/shared/area-bar-chart.component';
import { AvatarItem } from '@components/shared/avatar-item.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { Tile } from '@components/shared/tile.component';
import { PHASES_OF_OPERATIONS_LABELS, countries } from '@utils/constants';
import { EditIcon } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';

const formatRange = (range?: number[]): string => {
  if (range && range.length === 2) {
    return `${range[0]}-${range[1]}`;
  }
  if (range && range.length === 1) {
    return `${range[0]}`;
  }
  return '-';
};

const SectionTitle: React.FC<{ children: React.ReactNode; className?: string }> = (props) => {
  const { children, className } = props;
  return <h3 className={`text-sm font-normal text-gray-500 mb-2 ${className}`}>{children}</h3>;
};

type ExpertProfileDetailsProps = {
  expertProfile?: ExpertProfileSelectDto | null;
  onEditProfile?: () => void;
  user: UserSelectDto;
};

type SectionListItemsProps = {
  items?: { id: string; name: string }[];
};

const SectionListItems: React.FC<SectionListItemsProps> = (props) => {
  const { items } = props;
  if (!items || items.length === 0) return null;
  return (
    <div>
      <ul className="list-disc list-inside text-gray-700 space-y-1">
        {items.map((item) => (
          <li key={item.id}>{item.name}</li>
        ))}
      </ul>
    </div>
  );
};

export const ExpertProfileDetails: React.FC<ExpertProfileDetailsProps> = (props) => {
  const { user, expertProfile, onEditProfile } = props;

  const userName = user.profile?.fullName || user.email;

  const serviceName = useMemo(() => {
    if (!expertProfile) return 'Loading...';
    if (!expertProfile.service) {
      return expertProfile.type === ExpertProfileType.VETERAN ? 'Veteran' : 'Loading...';
    }
    return expertProfile.service.name;
  }, [expertProfile]);

  const userSubtitle = useMemo(() => {
    if (!expertProfile) return user.profile?.title || 'Technology Specialist';
    return expertProfile.type === ExpertProfileType.VETERAN
      ? serviceName
      : user.profile?.title || 'Technology Specialist';
  }, [expertProfile, serviceName, user.profile?.title]);

  const countryName = useMemo(() => {
    if (!expertProfile?.countryOfResidence) return '-';
    const country = countries.find((c) => c.code === expertProfile.countryOfResidence);
    return country ? country.name : expertProfile.countryOfResidence;
  }, [expertProfile]);

  const getCountryName = useCallback((countryCode: string) => {
    const country = countries.find((c) => c.code === countryCode);
    return country ? country.name : countryCode;
  }, []);

  return (
    <Tile
      className="w-full p-4 shadow-none"
      hoverReveal
    >
      <div>
        {/* Profile Header */}
        <section className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between w-full">
            <AvatarItem
              altText={userName}
              imageUrl={user.avatarUrl}
              subtitle={
                <p>
                  <span className="inline-block">{user.profile?.title}</span>
                  {expertProfile?.type !== ExpertProfileType.VETERAN && (
                    <span className="text-gray-400 inline-block pl-1">(non-military)</span>
                  )}
                </p>
              }
              title={userName}
            />
            <div className="flex flex-col items-end space-y-3">
              <div className="flex items-center gap-2">
                {onEditProfile && (
                  <IconButton.HoverReveal
                    ariaLabel="Edit profile"
                    icon={<EditIcon size={16} />}
                    size="lg"
                    onClick={onEditProfile}
                    variant="secondary"
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="space-y-4">
          {expertProfile?.type === ExpertProfileType.VETERAN && expertProfile?.service && (
            <section className="pb-4 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <AvatarItem
                  altText={expertProfile.service.name}
                  imageUrl={expertProfile.service.slug ? `/images/services/${expertProfile.service.slug}.png` : ''}
                  subtitle={
                    expertProfile.countryOfService && (
                      <p>
                        <span className="inline-block">{getCountryName(expertProfile.countryOfService)}</span>
                      </p>
                    )
                  }
                  title={expertProfile.service.name}
                />
              </div>
            </section>
          )}

          <section className="pb-4 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div className="pr-3">
                <SectionTitle>TRL Assessment Range</SectionTitle>
                <p className="text-base text-gray-800 font-medium">{formatRange(expertProfile?.trlAssessmentRange)}</p>
              </div>
              <div className="pl-3 flex flex-col items-end">
                <SectionTitle>MRL Assessment Range</SectionTitle>
                <p className="text-base text-gray-800 font-medium">{formatRange(expertProfile?.mrlAssessmentRange)}</p>
              </div>
            </div>
          </section>

          <section className="pb-4 space-y-4 border-b border-gray-200">
            <div className="flex justify-between gap-6">
              <div>
                <SectionTitle>Technology Categories</SectionTitle>
                {expertProfile?.categories && expertProfile.categories.length > 0 ? (
                  <BadgeList
                    items={expertProfile.categories.map((category) => ({
                      ...category,
                      tooltip: category.description,
                    }))}
                    badgeVariant="default"
                    limit={3}
                  />
                ) : (
                  <p className="text-gray-800 text-sm">-</p>
                )}
              </div>

              {typeof expertProfile?.yearsOfExperience === 'number' && (
                <div className="flex flex-col items-end">
                  <SectionTitle>YoE</SectionTitle>
                  <p className="text-base text-gray-800 font-medium">{expertProfile.yearsOfExperience}</p>
                </div>
              )}
            </div>

            <div>
              <SectionTitle>Operating Environments</SectionTitle>
              {expertProfile?.domains && expertProfile.domains.length > 0 ? (
                <BadgeList
                  items={expertProfile.domains}
                  badgeVariant="default"
                />
              ) : (
                <p className="text-gray-800 text-sm">-</p>
              )}
            </div>

            <div className="flex gap-6">
              <div>
                <SectionTitle>Country</SectionTitle>
                <p className="text-gray-800 text-sm">{countryName}</p>
              </div>

              <div>
                <SectionTitle>City</SectionTitle>
                <p className="text-gray-800 text-sm">
                  {expertProfile?.cityOfResidence || '-'}
                  {expertProfile?.stateOfResidence ? `, ${expertProfile.stateOfResidence}` : ''}
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              {expertProfile?.websiteUrl && (
                <div className="min-w-0 flex-1">
                  <SectionTitle>Website</SectionTitle>
                  <ExternalLink
                    className="text-sm block min-w-0"
                    href={expertProfile.websiteUrl}
                    iconSize={16}
                    title={expertProfile.websiteUrl}
                  >
                    {expertProfile.websiteUrl}
                  </ExternalLink>
                </div>
              )}
              {expertProfile?.linkedinUrl && (
                <div className="min-w-0 flex-1">
                  <SectionTitle>LinkedIn</SectionTitle>
                  <ExternalLink
                    className="text-sm block min-w-0"
                    href={expertProfile.linkedinUrl}
                    iconSize={16}
                    title={expertProfile.linkedinUrl}
                  >
                    {expertProfile.linkedinUrl}
                  </ExternalLink>
                </div>
              )}
            </div>
          </section>

          {expertProfile?.type === ExpertProfileType.VETERAN && (
            <section className="space-y-4">
              <div>
                <SectionTitle>Effects Expertise</SectionTitle>
                {expertProfile.effects && expertProfile.effects.length > 0 ? (
                  <BadgeList
                    items={expertProfile.effects}
                    badgeVariant="default"
                  />
                ) : (
                  <p className="text-gray-800 text-sm">-</p>
                )}
              </div>

              <div>
                <SectionTitle>Joint Capability Areas</SectionTitle>
                {expertProfile.capabilities && expertProfile.capabilities.length > 0 ? (
                  <SectionListItems items={expertProfile.capabilities} />
                ) : (
                  <p className="text-gray-800 text-sm">-</p>
                )}
              </div>

              <div>
                <SectionTitle>Functional Community Experience</SectionTitle>
                {expertProfile.communities && expertProfile.communities.length > 0 ? (
                  <SectionListItems items={expertProfile.communities} />
                ) : (
                  <p className="text-gray-800 text-sm">-</p>
                )}
              </div>

              <div>
                <SectionTitle className="mb-3">Phases of Operation</SectionTitle>
                {expertProfile.phases ? (
                  <AreaBarChartComponent
                    customLabels={PHASES_OF_OPERATIONS_LABELS}
                    items={[
                      {
                        title: userName,
                        subtitle: userSubtitle,
                        valueRange: { min: expertProfile.phases[0], max: expertProfile.phases[1] },
                      },
                    ]}
                    valueRange={{ max: 5, min: 0 }}
                  />
                ) : (
                  <p className="text-gray-800 text-sm">-</p>
                )}
              </div>
            </section>
          )}

          <section>
            <SectionTitle className="mb-3">Education</SectionTitle>
            {expertProfile?.educations && expertProfile.educations.length > 0 ? (
              <ul className="space-y-1">
                {expertProfile.educations.map((edu, index) => (
                  <li
                    key={index}
                    className="text-gray-700"
                  >
                    <span className="font-semibold">{edu.title}</span> @ {edu.institute}
                    {(edu.startYear || edu.endYear) && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({edu.startYear}
                        {edu.endYear ? ` - ${edu.endYear}` : ''})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-800 text-sm">-</p>
            )}
          </section>
        </div>
      </div>
    </Tile>
  );
};
